import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReferralPageBannerService } from '../../home/referral-page-banner/referral-page-banner.service';
import { SelectedSurveyQuestionOptions } from '../../teacher-feedback/teacher-feedback-service/selected-survey-question-options';
import { BackboneAdapterService } from '../backbone-adapter.service';
import { LiveClassSurveyService } from '../live-class-survey.service';
import { MODAL_DATA } from '../tokens/modal-data.token';
import { NotificationDto } from '../types/notification-dto';
import {
  SurveyDto,
  SurveyOptionDto,
  SurveyOptionType,
  SurveyQuestionDto,
  SurveyQuestionType,
} from '../types/survey-dto';
import {
  SatisfactionAnswerType
} from './live-class-survey-data-types';
import { SurveyId } from '../types/survey-id.enum';

@Component({
  selector: 'lp2-live-class-survey',
  templateUrl: './live-class-survey.component.html',
  styleUrls: ['./live-class-survey.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveClassSurveyComponent implements OnInit {
  public surveyData: SurveyDto;
  public hasFailedFetching = false;

  public questionTypeEnum = SurveyQuestionType;
  public optionTypeEnum = SurveyOptionType;

  public isFetching = true;
  public isSaving = false;
  public wasSaved = false;

  public imageAnswer: SurveyOptionDto;

  public displayReferralBanner$ = this.referralPageBannerService.isElligible$.pipe(
    map((eligible) => eligible && this.imageAnswer.questionOption === SatisfactionAnswerType.HAPPY)
  );

  private answersArray: SelectedSurveyQuestionOptions[] = [];

  private multiAnswersCount = 0;

  constructor(
    private readonly liveClassSurveyService: LiveClassSurveyService,
    private readonly ngbActiveModal: NgbActiveModal,
    private readonly referralPageBannerService: ReferralPageBannerService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly backbone: BackboneAdapterService,
    @Inject(MODAL_DATA) public readonly notification: NotificationDto
  ) {}

  ngOnInit() {
    this.getSurvey();
  }

  public submitAnswer() {
    if (!this.imageAnswer) {
      return;
    }
    this.isSaving = true;
    this.liveClassSurveyService.saveSurvey(this.answersArray, this.notification.liveSessionId).subscribe(() => {
      this.wasSaved = true;
      this.isSaving = false;
      this.cdRef.detectChanges();
    });
  }

  public closeModal() {
    this.ngbActiveModal.close();
    this.backbone.triggerEvent('notifications:onHide');
  }

  public setAnswer(question: SurveyQuestionDto, option: SurveyOptionDto) {
    const index = this.getAnswerIndex(question, option);

    switch (question.type) {
      case SurveyQuestionType.IMAGE:
        if (option === this.imageAnswer) {
          return;
        }
        if (index === -1) {
          this.addAnswer(question, option);
        } else {
          this.updateAnswer(index, option);
        }
        this.imageAnswer = option;
        break;
      case SurveyQuestionType.SINGLE:
        if (index === -1) {
          this.addAnswer(question, option);
        } else {
          this.updateAnswer(index, option);
        }
        break;
      case SurveyQuestionType.MULTI:
        if (index === -1) {
          this.addAnswer(question, option);
          this.multiAnswersCount++;
        } else {
          this.removeAnswer(index);
          this.multiAnswersCount--;
        }
        break;
    }
  }

  private getAnswerIndex(question?: SurveyQuestionDto, option?: SurveyOptionDto) {
    return this.answersArray.findIndex(({ profileQuestionId, profileQuestionOptionId }) => {
      if (question.type === SurveyQuestionType.MULTI) {
        return profileQuestionOptionId === option.id;
      }
      if (question.type === SurveyQuestionType.SINGLE || question.type === SurveyQuestionType.IMAGE) {
        return profileQuestionId === question.id;
      }
    });
  }

  private addAnswer(question: SurveyQuestionDto, option: SurveyOptionDto) {
    this.answersArray.push({ profileQuestionId: question.id, profileQuestionOptionId: option.id });
  }

  private removeAnswer(index: number) {
    this.answersArray.splice(index, 1);
  }

  private updateAnswer(index: number, option: SurveyOptionDto): void {
    this.answersArray[index].profileQuestionOptionId = option.id;
  }

  private getSurvey() {
    combineLatest([
      this.liveClassSurveyService.getSurveyDetails(this.notification.liveSessionId),
      this.liveClassSurveyService.getSurveyQuestions(SurveyId.LIVE_CLASS_SURVEY_NEW),
    ]).subscribe(([details, questions]) => {
      if (!details || !questions) {
        this.hasFailedFetching = true;
        this.surveyData = null;
      } else {
        this.surveyData = {
          details,
          groups: questions,
        };
      }

      this.isFetching = false;
      this.cdRef.detectChanges();
    });
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/live-class-survey/live-class-survey.component.ts